import React, { useContext, useEffect } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { useImpression, ImpressionProvider } from '@thd-olt-component-react/impression';
import { withHydrator } from '@thd-olt-component-react/hydrator';
import { withDynamicComponent } from '@thd-nucleus/app-render';
import { withErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { initSurvey } from '../qualtricsHelper';
import { SearchFeedbackPlaceholder } from './SearchFeedbackPlaceholder/SearchFeedbackPlaceholder';
import './search-feedback.scss';

const SearchFeedbackComponent = ({
  type = 'search',
  impressionID = null
}) => {
  const { channel } = useContext(ExperienceContext);

  const { ref, clickID } = useImpression({
    data: {
      id: impressionID || 'SearchFeedbackID',
      name: 'SearchFeedback',
      component: 'SearchFeedback',
      position: 1,
      type: 'content'
    }
  });

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('search-feedback.ready');
    initSurvey();
  }, []);

  if (channel === 'mobile') return null;

  const rowClasses = classNames({
    collectionFeedbackWrapper: type === 'collection'
  });

  const columnClasses = classNames({
    'article-feedback-wrapper': type === 'articles',
    'search-feedback-wrapper': type !== 'articles'
  });

  return (
    <div
      className="sui-grid"
      ref={ref}
      // eslint-disable-next-line react/no-unknown-property
      clickid={clickID}
    >
      <div
        className={rowClasses}
        data-component="SearchFeedback"

      >
        <div className={columnClasses}>
          <div id={`${type}Feedback`} data-testid={`${type}Feedback`} />
        </div>
      </div>
    </div>
  );
};

SearchFeedbackComponent.displayName = 'SearchFeedbackComponent';

SearchFeedbackComponent.propTypes = {
  type: PropTypes.oneOf([
    'articles',
    'search',
    'collection'
  ]),
  impressionID: PropTypes.string
};

const SearchFeedback = ({
  type = 'search',
  impressionID = null,
  data = null,
  ...restProps
}) => {
  const fallbackData = {
    id: impressionID || 'SearchFeedbackID',
    component: 'SearchFeedback',
    name: 'SearchFeedback',
    type: 'content'
  };

  const impressionData = data || fallbackData;

  return (
    <ImpressionProvider
      key="SearchFeedback"
      data={impressionData}
    >
      <SearchFeedbackComponent
        type={type}
        impressionID={impressionID}
      />
    </ImpressionProvider>
  );
};

const placeholder = (
  <SearchFeedbackPlaceholder />
);

SearchFeedback.displayName = 'SearchFeedback';

SearchFeedback.propTypes = {
  type: PropTypes.oneOf([
    'articles',
    'search',
    'collection'
  ]),
  impressionID: PropTypes.string,
  data: PropTypes.shape({
    id: PropTypes.string,
    component: PropTypes.string,
    name: PropTypes.string,
    type: PropTypes.string
  })
};

const HydratedSearchFeedback = withHydrator({
  id: 'feedback',
  scrollBuffer: 750,
  delay: 2000,
  preserveCtxVal: 'clientStore',
  placeholder,
}, SearchFeedback);

const DynamicSearchFeedback = withDynamicComponent(HydratedSearchFeedback, { placeholder });
const SearchFeedbackWithBoundary = withErrorBoundary(DynamicSearchFeedback);

SearchFeedbackWithBoundary.propTypes = SearchFeedback.propTypes;
SearchFeedbackWithBoundary.displayName = SearchFeedback.displayName;

export { SearchFeedbackWithBoundary as SearchFeedback };
